import dayjs, { Dayjs } from "dayjs";
import { useFormik } from "formik";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";

import { ICreateAppointmentRequest } from "../../../../@types/Appointment";
import { Notification } from "../../../../@types/Notification";
import { Button } from "../../../../components/Button";
import { DateTimePickerInput } from "../../../../components/DateTimePicker";
import { IOption, SelectDropdown } from "../../../../components/SelectDropdown";
import { TextField } from "../../../../components/TextField";
import { PATIENT_LIST_PAGE_SIZE } from "../../../../constants/pagination";
import { usePatientList } from "../../../../hooks/queries/patients";
import { getTableHospitalStaff } from "../../../../hooks/queries/staff";
import { useUserPreferences } from "../../../../hooks/useUserPreferences";
import { appointmentsService } from "../../../../services/appointmentsService";
import {
  extractErrors,
  getSummaryErrorMessage,
} from "../../../../utils/apiErrorUtils";
import { mixpanelActions } from "../../../../utils/mixpanel";
import { isValidHttpUrl, MAX_URL_LENGTH } from "../../../../utils/url";
import { CancelButton } from "../../../Settings/components/SecurityModal/SecurityModal.styles";
import {
  ContentContainer,
  ErrorMessage,
  ModalContainer,
  Row,
  submitButtonCss,
  SubmitContainer,
  Title,
  urlInputCss,
} from "./CreateAppointmentModal.styles";

type CreateAppointmentModalProps = {
  onClose: (notification?: Notification, refetchTable?: boolean) => void;
  // When opened from a patient's appointments section the patient is already
  // known, so we pre-fill and lock the patient selector.
  prefilledPatientId?: number;
};

export function CreateAppointmentModal({
  onClose,
  prefilledPatientId,
}: CreateAppointmentModalProps) {
  const { t } = useTranslation();
  const { patients } = usePatientList(
    { variant: "active" },
    { pageSize: PATIENT_LIST_PAGE_SIZE, refetchOnMount: false }
  );
  const { staff } = getTableHospitalStaff();
  const { staffPreferences } = useUserPreferences();

  const [isLoading, setIsLoading] = useState(false);
  const isSubmittingRef = useRef(false);

  const hasHcpAppointmentUrl = Boolean(staffPreferences?.hcpAppointmentUrl);

  const handleOnSubmitForm = async ({
    patientId,
    staffId,
    duration,
    appointmentUrl,
    hcpAppointmentUrl,
    date,
  }: any) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      setIsLoading(true);

      const request: ICreateAppointmentRequest = {
        patientId: parseInt(patientId, 10),
        staffId: parseInt(staffId, 10),
        durationInMinutes: parseInt(duration, 10),
        appointmentUrl,
        hcpAppointmentUrl: hasHcpAppointmentUrl ? hcpAppointmentUrl : null,
        date,
      };

      const notification: Notification = {
        show: true,
        message: "",
        type: "success",
        width: "max-content",
      };

      let refetchTable = false;

      const result = await appointmentsService.createAppointment(request);

      setIsLoading(false);

      if (result.status >= 200 && result.status < 300) {
        mixpanelActions.track("Event: AppointmentCreated");
        notification.message = t("appointments_toasts_created");
        refetchTable = true;
        onClose(notification, refetchTable);
      } else {
        // Prefer the API's own validation message (e.g. "Appointment URL must
        // be a valid http(s) URL.") over a generic failure, so a divergence
        // between this form's rules and the server's is legible to the user.
        const apiErrors = extractErrors(result);

        notification.message =
          apiErrors.length > 0
            ? getSummaryErrorMessage(apiErrors)
            : t("appointments_toasts_createFailed");
        notification.type = "error";
        onClose(notification, refetchTable);
      }
    } finally {
      isSubmittingRef.current = false;
    }
  };

  // Mirrors the server-side rule on POST /Shared/Appointments so the user gets
  // an inline error instead of a raw 400. This is defence in depth and a UX
  // affordance, not the security boundary — the API is (EN-5095).
  const httpUrlRule = (fieldLabel: string) =>
    yup
      .string()
      .max(
        MAX_URL_LENGTH,
        t("appointments_validation_urlTooLong", {
          field: fieldLabel,
          max: MAX_URL_LENGTH,
        })
      )
      .test(
        "is-http-url",
        t("appointments_validation_urlInvalid", { field: fieldLabel }),
        // Leave presence to `.required()` / the field being optional: an empty
        // value must not also trip the scheme error.
        (value) => !value || isValidHttpUrl(value)
      );

  const formValidationSchema = yup.object().shape({
    patientId: yup
      .string()
      .required(t("appointments_validation_patientRequired")),
    staffId: yup.string().required(t("appointments_validation_hcpRequired")),
    duration: yup
      .string()
      .required(t("appointments_validation_durationRequired")),
    appointmentUrl: httpUrlRule(
      t("appointments_fields_appointmentUrl")
    ).required(t("appointments_validation_appointmentUrlRequired")),
    // Optional by design: an empty HCP URL means the clinician falls back to
    // the patient URL. When present it still has to be a valid http(s) URL.
    hcpAppointmentUrl: hasHcpAppointmentUrl
      ? httpUrlRule(t("appointments_fields_hcpAppointmentUrl")).required(
          t("appointments_validation_hcpAppointmentUrlRequired")
        )
      : httpUrlRule(t("appointments_fields_hcpAppointmentUrl")),
    date: yup.date().required(t("appointments_validation_dateRequired")),
  });

  const formik = useFormik({
    initialValues: {
      patientId: prefilledPatientId ? prefilledPatientId.toString() : undefined,
      staffId: undefined,
      duration: undefined,
      appointmentUrl: staffPreferences?.defaultAppointmentUrl ?? undefined,
      hcpAppointmentUrl: staffPreferences?.hcpAppointmentUrl ?? undefined,
      date: dayjs(),
    },
    onSubmit: handleOnSubmitForm,
    validationSchema: formValidationSchema,
  });

  const patientsSelect = useMemo(() => {
    const patientOptions = patients?.map((patient) => {
      return {
        label: `${patient.firstName} ${patient.lastName}`,
        value: patient.id.toString(),
        key: uuidv4(),
      } as IOption;
    });

    return (
      <SelectDropdown
        showSearch
        disabled={prefilledPatientId !== undefined}
        options={patientOptions ?? []}
        width={240}
        placeholder={t("appointments_createModal_selectPatient")}
        optionFilterProp="label"
        value={formik.values.patientId}
        onValueChange={(value: string | string[]) => {
          formik.setFieldValue("patientId", value.toString());
        }}
      />
    );
  }, [patients, t]);

  const hospitalStaffSelect = useMemo(() => {
    const staffOptions = staff?.map((staff) => {
      return {
        label: staff.name,
        value: staff.id.toString(),
        key: uuidv4(),
      } as IOption;
    });

    return (
      <SelectDropdown
        showSearch
        options={staffOptions ?? []}
        width={240}
        placeholder={t("appointments_createModal_selectHcp")}
        optionFilterProp="label"
        value={formik.values.staffId}
        onValueChange={(value: string | string[]) => {
          formik.setFieldValue("staffId", value.toString());
        }}
      />
    );
  }, [staff, t]);

  const durationSelect = useMemo(() => {
    const durationOptions: IOption[] = [
      {
        label: "15 minutes",
        value: "15",
        key: uuidv4(),
      },
      {
        label: "30 minutes",
        value: "30",
        key: uuidv4(),
      },
      {
        label: "45 minutes",
        value: "45",
        key: uuidv4(),
      },
      {
        label: "60 minutes",
        value: "60",
        key: uuidv4(),
      },
    ];

    return (
      <SelectDropdown
        options={durationOptions}
        width={240}
        placeholder={t("appointments_createModal_selectDuration")}
        value={formik.values.duration}
        onValueChange={(value: string | string[]) => {
          formik.setFieldValue("duration", value.toString());
        }}
      />
    );
  }, [t]);

  const shouldShowErrorMessage = (
    field:
      | "staffId"
      | "patientId"
      | "duration"
      | "date"
      | "appointmentUrl"
      | "hcpAppointmentUrl"
  ): string => {
    let error = "";

    if (formik.errors[field] !== undefined) {
      error = ((formik.errors[field] as string) ?? "").toString();
    }
    return formik.touched[field] ? error : "";
  };

  return (
    <ModalContainer>
      <Title>{t("appointments_createModal_title")}</Title>

      <ContentContainer>
        <Row>
          <p>{t("appointments_hcp")}</p>
          {hospitalStaffSelect}
          <ErrorMessage>{shouldShowErrorMessage("staffId")}</ErrorMessage>
        </Row>
        <Row>
          <p>{t("appointments_createModal_patient")}</p>
          {patientsSelect}
          <ErrorMessage>{shouldShowErrorMessage("patientId")}</ErrorMessage>
        </Row>
        <Row>
          <p>{t("appointments_createModal_duration")}</p>
          {durationSelect}
          <ErrorMessage>{shouldShowErrorMessage("duration")}</ErrorMessage>
        </Row>
        <Row>
          <p>{t("appointments_createModal_appointmentUrl")}</p>
          <TextField
            label=""
            onChange={formik.handleChange}
            name="appointmentUrl"
            placeholder={t("appointments_createModal_pasteUrl")}
            backgroudColor="white"
            value={formik.values.appointmentUrl}
            className={urlInputCss()}
          />
          <ErrorMessage>
            {shouldShowErrorMessage("appointmentUrl")}
          </ErrorMessage>
        </Row>
        {hasHcpAppointmentUrl && (
          <Row>
            <p>{t("appointments_createModal_hcpUrl")}</p>
            <TextField
              label=""
              onChange={formik.handleChange}
              name="hcpAppointmentUrl"
              placeholder={t("appointments_createModal_pasteUrl")}
              backgroudColor="white"
              value={formik.values.hcpAppointmentUrl}
              className={urlInputCss()}
            />
            <ErrorMessage>
              {shouldShowErrorMessage("hcpAppointmentUrl")}
            </ErrorMessage>
          </Row>
        )}
        <Row>
          <p>{t("appointments_createModal_date")}</p>
          <DateTimePickerInput
            defaultValue={formik.values.date}
            onChange={(value: Dayjs) => formik.setFieldValue("date", value)}
            width="240px"
          />
          <ErrorMessage>{shouldShowErrorMessage("date")}</ErrorMessage>
        </Row>
      </ContentContainer>

      <SubmitContainer>
        <Button
          label={t("appointments_createModal_submit")}
          type="submit"
          onClick={formik.submitForm}
          isLoading={isLoading}
          className={submitButtonCss()}
        />

        <CancelButton
          onClick={() => {
            onClose();
            mixpanelActions.track("Event: CreateCancelled");
          }}
          style={{ marginLeft: "-35px", padding: "0 30px" }}
        >
          {t("common_cancel")}
        </CancelButton>
      </SubmitContainer>
    </ModalContainer>
  );
}
